import { useState } from "react";
import { X, Plus, Trash2, Play, Loader2, CheckCircle, XCircle, Cable, GitBranch } from "lucide-react";
import EndpointPicker from "./EndpointPicker";
import { testStep } from "../../services/workflowService";

/**
 * Right-hand step inspector. Configure (endpoint + inputs / branch conditions)
 * and Routing tabs, plus a per-step Test. `onChangeStep(updatedStep)` patches
 * the step in the parent draft. Read-only when `editable` is false.
 */
export default function StepDrawer({
  stepName,
  step,
  allStepNames,
  endpoints,
  endpointsLoading,
  editable,
  onChangeStep,
  onClose
}) {
  const [tab, setTab] = useState("configure");
  const [newKey, setNewKey] = useState("");
  const [testing, setTesting] = useState(false);
  const [testRes, setTestRes] = useState(null);

  if (!step) return null;
  const isBranch = step.type === "branch";
  const payload = step.request_payload || {};
  const otherSteps = allStepNames.filter((n) => n !== stepName);

  const patch = (updates) => onChangeStep({ ...step, ...updates });

  const runTest = async () => {
    setTesting(true);
    setTestRes(null);
    const res = await testStep({
      endpoint_id: step.endpoint_id,
      request_payload: payload,
      context: { inputs: {}, steps: {} }
    });
    setTesting(false);
    setTestRes(res.success ? res.data : { status: "error", error_message: res.message });
  };

  return (
    <div className="w-[360px] shrink-0 border-l border-gray-200 bg-white flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${isBranch ? "bg-violet-50 text-violet-600" : "bg-blue-50 text-blue-600"}`}>
          {isBranch ? <GitBranch size={14} /> : <Cable size={14} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-sm font-semibold text-gray-900 truncate">{stepName}</div>
          <div className="text-[11px] text-gray-400">{isBranch ? "branch" : "endpoint call"}</div>
        </div>
        <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        {["configure", "routing"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`flex-1 text-sm py-2 font-medium capitalize border-b-2 -mb-px ${
              tab === t ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-auto p-4 space-y-4">
        {tab === "configure" && !isBranch && (
          <>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Endpoint</label>
              <EndpointPicker
                endpoints={endpoints}
                loading={endpointsLoading}
                value={step.endpoint_id}
                disabled={!editable}
                onChange={(id) => patch({ endpoint_id: id })}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Inputs</label>
              {Object.keys(payload).length === 0 && (
                <p className="text-xs text-gray-400 mb-2">No inputs yet.</p>
              )}
              <div className="space-y-1.5">
                {Object.entries(payload).map(([k, v]) => (
                  <div key={k} className="grid grid-cols-[1fr_1fr_28px] gap-1.5 items-center">
                    <span className="font-mono text-xs text-gray-500 truncate" title={k}>{k}</span>
                    <input
                      value={String(v)}
                      disabled={!editable}
                      onChange={(e) =>
                        patch({ request_payload: { ...payload, [k]: e.target.value } })
                      }
                      placeholder="{{ inputs.x }}"
                      className="font-mono text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                    />
                    {editable && (
                      <button
                        onClick={() => {
                          const { [k]: _, ...rest } = payload;
                          patch({ request_payload: rest });
                        }}
                        className="p-1 text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {editable && (
                <div className="flex gap-1.5 mt-2">
                  <input
                    value={newKey}
                    onChange={(e) => setNewKey(e.target.value)}
                    placeholder="new input key (e.g. path.vin)"
                    className="flex-1 font-mono text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={() => {
                      const key = newKey.trim();
                      if (!key || key in payload) return;
                      patch({ request_payload: { ...payload, [key]: "" } });
                      setNewKey("");
                    }}
                    className="px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Test */}
            <div className="pt-2 border-t border-gray-100">
              <button
                onClick={runTest}
                disabled={testing || !step.endpoint_id}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50"
              >
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                Test step
              </button>
              <p className="text-[11px] text-gray-400 mt-1">
                Calls this endpoint for real with your org’s creds.
              </p>
              {testRes && (
                <div className="mt-2">
                  <div className="flex items-center gap-1.5 mb-1">
                    {testRes.status === "success" || testRes.status === "succeeded" ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500" />
                    )}
                    <span className="text-xs font-semibold text-gray-700">
                      {testRes.status} {testRes.http_status_code ? `· ${testRes.http_status_code}` : ""}
                    </span>
                  </div>
                  {testRes.error_message && (
                    <p className="text-[11px] text-red-600 break-words mb-1">{testRes.error_message}</p>
                  )}
                  {testRes.response_body != null && (
                    <pre className="text-[10px] bg-gray-50 border border-gray-200 rounded p-2 overflow-auto max-h-40">
                      {JSON.stringify(testRes.response_body, null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* branch conditions live under Configure */}
        {tab === "configure" && isBranch && (
          <BranchConfig step={step} otherSteps={otherSteps} editable={editable} patch={patch} />
        )}

        {tab === "routing" && !isBranch && (
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Runs next</label>
            {(step.next || []).length === 0 && (
              <p className="text-xs text-gray-400 mb-2">Nothing wired. Drag from this node on the canvas, or add below.</p>
            )}
            <div className="space-y-1.5">
              {(step.next || []).map((n, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <span className="flex-1 font-mono text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded px-2 py-1">{n}</span>
                  {editable && (
                    <button
                      onClick={() => patch({ next: step.next.filter((_, idx) => idx !== i) })}
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {editable && (
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value) patch({ next: [...(step.next || []), e.target.value] });
                }}
                className="mt-2 w-full text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">+ add next step…</option>
                {otherSteps.filter((n) => !(step.next || []).includes(n)).map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            )}
          </div>
        )}

        {tab === "routing" && isBranch && (
          <p className="text-xs text-gray-400">Branch routing is set by its conditions (Configure tab).</p>
        )}
      </div>
    </div>
  );
}

function BranchConfig({ step, otherSteps, editable, patch }) {
  const branches = step.branches || [];
  return (
    <div className="space-y-3">
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">Conditions</label>
      {branches.map((b, i) => (
        <div key={i} className="rounded-lg border border-gray-200 p-2 space-y-1.5">
          <textarea
            rows={2}
            value={b.when}
            disabled={!editable}
            onChange={(e) =>
              patch({ branches: branches.map((x, idx) => (idx === i ? { ...x, when: e.target.value } : x)) })
            }
            placeholder="{{ 'Commercial' in steps.verify.response_body.data[0].classificationName }}"
            className="w-full font-mono text-[11px] border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
          />
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-gray-400">→</span>
            <select
              value={b.next}
              disabled={!editable}
              onChange={(e) =>
                patch({ branches: branches.map((x, idx) => (idx === i ? { ...x, next: e.target.value } : x)) })
              }
              className="flex-1 text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
            >
              <option value="">choose step…</option>
              {otherSteps.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            {editable && (
              <button
                onClick={() => patch({ branches: branches.filter((_, idx) => idx !== i) })}
                className="p-1 text-gray-400 hover:text-red-600"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      ))}
      {editable && (
        <button
          onClick={() => patch({ branches: [...branches, { when: "", next: "" }] })}
          className="flex items-center gap-1.5 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded"
        >
          <Plus className="w-4 h-4" /> Add condition
        </button>
      )}
      <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Otherwise (default)</label>
        <select
          value={step.default_next || ""}
          disabled={!editable}
          onChange={(e) => patch({ default_next: e.target.value || null })}
          className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
        >
          <option value="">— none —</option>
          {otherSteps.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
    </div>
  );
}
